import { ReverseSplitForShareholder, ReverseSplitForShareholderData } from "@/types/Decisions";
import { FractionShareAdjustment } from "@/types/Transactions";
import { parseInputString } from "@/utils/numberInput";
import { anyValidSplit, closestReverse, closestReverseWithoutFractions } from "@/utils/shareSplits";
import { makeAutoObservable } from "mobx";
import {
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  extractSectionWithMaxScoreFromArticles,
  sharesForShareholderByShareTypeCode,
  sharesForShareholderByShareType,
  total,
} from "@/dbot/common/commonDomain";

import { compact, identity } from "lodash";

import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import {
  ArticleSelectorModel,
  NumberFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { ShareType } from "@/common/share";

export enum ShareReverseSplitFractionAdjustment {
  ONE_PERSON = "ONE_PERSON",
  HIGHEST_FRACTION = "highestFraction",
  MANUAL_ADJUSTMENT = "manualAdjustment",
}
const shareReverseSplitFractionAdjustmentCaptions: {
  [key in ShareReverseSplitFractionAdjustment]: TMessage;
} = {
  [ShareReverseSplitFractionAdjustment.ONE_PERSON]: {
    key: "decisions.shareSplits.reverse-split.how-to-adjust-fractions.onePerson",
  },
  [ShareReverseSplitFractionAdjustment.HIGHEST_FRACTION]: {
    key: "decisions.shareSplits.reverse-split.how-to-adjust-fractions.highestFraction",
  },
  [ShareReverseSplitFractionAdjustment.MANUAL_ADJUSTMENT]: {
    key: "decisions.shareSplits.reverse-split.how-to-adjust-fractions.manualAdjustment",
  },
};

export type ReverseSplitShare = {
  type: DecisionType.reverseSplitShare;
  ratio: number;
  shareCapitalParagraph?: number;
  fractionAdjustment: ShareReverseSplitFractionAdjustment | undefined;
  fractionAdjustmentName?: string;
  fractionShareAdjustments: FractionShareAdjustment[];
};

export const reverseSplitShare = (data?: Partial<ReverseSplitShare>): ReverseSplitShare => ({
  type: DecisionType.reverseSplitShare,
  ratio: data?.ratio ?? DEFAULT_REVERSE_SPLIT_RATIO,
  shareCapitalParagraph: data?.shareCapitalParagraph,
  fractionAdjustment: data?.fractionAdjustment,
  fractionAdjustmentName: data?.fractionAdjustmentName,
  fractionShareAdjustments: data?.fractionShareAdjustments ?? [],
});

export const DEFAULT_REVERSE_SPLIT_RATIO = 1;

class ReverseSplitShareForm {
  readonly type = DecisionType.reverseSplitShare;

  dispatch: DbotEventDispatcher;
  context: DecisionContext<ReverseSplitShare>;
  data: ReverseSplitShare;

  ratio: NumberFieldModel;
  shareCapitalParagraph: ArticleSelectorModel;
  fractionAdjustment?: ShareReverseSplitFractionAdjustment;
  fractionAdjustmentNameModel: RadioFieldModel<string | undefined>;
  fractionShareAdjustments: FractionShareAdjustment[] = [];
  reverseSplitForShareholders: ReverseSplitForShareholder[] = [];
  shareReverseSplitAdjustByHighestFractionPossible = false;
  shareReverseSplitFractionAdjustmentModel: RadioFieldModel<
    ShareReverseSplitFractionAdjustment | undefined
  >;

  reverseSplitShareOverview: ReverseSplitShareOverview;

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext<ReverseSplitShare>,
    data: ReverseSplitShare = reverseSplitShare(),
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.data = data;

    this.ratio = new NumberFieldModel({
      value: data?.ratio,
      isPositive: true,
      isInteger: true,
      required: true,
    });

    this.shareCapitalParagraph = new ArticleSelectorModel(
      context.inputState?.articles,
      data?.shareCapitalParagraph,
    );

    this.shareReverseSplitFractionAdjustmentModel = new RadioFieldModel<
      ShareReverseSplitFractionAdjustment | undefined
    >({
      value: data?.fractionAdjustment,
      options: Object.values(ShareReverseSplitFractionAdjustment),
      idFn: identity,
      labelFn: (value) =>
        value ? shareReverseSplitFractionAdjustmentCaptions[value] : { translated: "" },
      onChange: (value) => this.changeShareReverseSplitFractionAdjustment(value),
    });

    this.fractionAdjustment = data?.fractionAdjustment;
    this.fractionAdjustmentNameModel = new RadioFieldModel<string | undefined>({
      value: data?.fractionAdjustmentName,
      options: generateNameButtonsForFractionAdjustment(this),
      idFn: identity,
      labelFn: (option) => {
        return { translated: option ?? "" };
      },
    });
    new StringFieldModel({ value: data?.fractionAdjustmentName });
    this.fractionShareAdjustments = data?.fractionShareAdjustments ?? [];
    this.reverseSplitForShareholders = buildReverseSplitForShareholders(
      this,
      data.fractionShareAdjustments,
    );

    this.reverseSplitShareOverview = new ReverseSplitShareOverview(
      this.reverseSplitForShareholders,
    );
  }

  init() {
    if (this.data.shareCapitalParagraph === undefined) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success":
        if (!this.shareCapitalParagraph.selected) {
          this.shareCapitalParagraph.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SHARE_CAPITAL"),
          );
        }
        break;
    }
  }

  get parsedRatio(): number | undefined {
    return parseInputString(this.ratio.inputValue, this.ratio.locale);
  }

  get someShareTypeSplitWithRemainder(): boolean {
    return this.shareTypes
      .filter((shareType) => this.shareSumByType(shareType) > 0)
      .some(
        (shareType) => this.parsedRatio && this.shareSumByType(shareType) % this.parsedRatio !== 0,
      );
  }

  get someShareholderContainingFractions(): boolean {
    return this.reverseSplitForShareholders.some(
      (shareholder) => !!shareholder.shareTypesContainingFractions.length,
    );
  }

  get closestSplitWithoutFractions() {
    if (this.parsedRatio) {
      return closestReverseWithoutFractions(
        this.shareholders,
        this.parsedRatio,
        this.sharesForShareholders,
      );
    }
  }

  get closestReverse() {
    if (this.parsedRatio) {
      return closestReverse(this.numberOfShares, this.parsedRatio);
    }
  }

  get invalidReverseSplitIntoOneMessage(): TMessage | undefined {
    if (anyValidSplit(this.currentNominalValue) && this.parsedRatio === 1) {
      return {
        key: "decisions.shareSplits.reverse-split.helperText-mergeIntoOne",
      };
    }
  }

  get invalidReverseSplitMessage(): TMessage | undefined {
    if (this.parsedRatio && this.parsedRatio > 1 && this.someShareTypeSplitWithRemainder) {
      return {
        key: "decisions.shareSplits.reverse-split.helperText-invalid",
        params: {
          n: this.closestSplitWithoutFractions || this.closestReverse || "",
        },
      };
    }
  }

  get invalidFractionMessage(): TMessage | undefined {
    if (
      this.parsedRatio &&
      this.parsedRatio > 1 &&
      !this.someShareTypeSplitWithRemainder &&
      this.someShareholderContainingFractions
    ) {
      return {
        key: "decisions.shareSplits.reverse-split.helperText-invalid-fractions",
      };
    }
  }

  get currentNominalValue() {
    return this.context.inputState?.sharenominalValue ?? 0;
  }

  get newNominalValue() {
    const ratio = this.parsedRatio;

    if (ratio === undefined) {
      return this.currentNominalValue;
    }
    return this.currentNominalValue * ratio;
  }

  get articlesOfAssociation() {
    return this.context.inputState?.articles;
  }

  get shareholders() {
    return this.context.initialState.shareholders;
  }

  get sharesForShareholders() {
    return this.context.inputState?.sharesForShareholders;
  }

  get numberOfShares() {
    return this.context.inputState?.numberOfShares ?? [];
  }

  shareSumByType(shareType: ShareType) {
    return total(
      this.numberOfShares?.filter((shares) => shares.shareTypeId === shareType.sharetypeId),
    );
  }

  get shareTypes() {
    return this.context.initialState.shareTypes;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return (
      this.ratio.isValid &&
      this.shareCapitalParagraph.isValid &&
      this.fractionAdjustment === undefined &&
      !this.invalidReverseSplitMessage
    );
  }

  setReverseSplitRatio(value: string) {
    this.ratio.setInputValue(value);
    const n = this.parsedRatio;
    if (n !== undefined) {
      const { reverseSplitForShareholders } = changeReverseSplitRatio(this, n);

      this.ratio.setAndCommitInputValue(n);
      this.setReverseSplitForShareholders(reverseSplitForShareholders);

      this.reverseSplitShareOverview.updateAfterChangeSplit(reverseSplitForShareholders);
    }
  }

  setReverseSplitForShareholders(value: ReverseSplitForShareholder[]) {
    this.reverseSplitForShareholders = [...value];
  }

  changeShareReverseSplitFractionAdjustment(value?: ShareReverseSplitFractionAdjustment) {
    if (value === ShareReverseSplitFractionAdjustment.MANUAL_ADJUSTMENT) {
      this.reverseSplitShareOverview.setIsOpen(!this.reverseSplitShareOverview.isOpen);
    } else if (value === ShareReverseSplitFractionAdjustment.HIGHEST_FRACTION) {
      let fractionShares: {
        id: number;
        name: string;
        shares: { id: number; name: string; numberOfShares: number; fractions: number }[];
      }[] = [];

      this.shareTypes
        .filter((shareType) => this.shareSumByType(shareType) > 0)
        .map((shareType) => {
          let shares: {
            id: number;
            name: string;
            numberOfShares: number;
            fractions: number;
          }[] = [];

          this.reverseSplitForShareholders
            .filter((shareholder: ReverseSplitForShareholder) =>
              shareholder.shareTypesContainingFractions.includes(shareType.shareTypeCode),
            )
            .map((shareholder) => {
              shareholder.data
                .filter(
                  (data) => data.fractions !== 0 && data.shareTypeId === shareType.sharetypeId,
                )
                .map((share) => {
                  shares = [
                    ...shares,
                    {
                      id: shareholder.id,
                      name: shareholder.name,
                      numberOfShares: share.newNumberOfShares,
                      fractions: share.fractions,
                    },
                  ];
                });
            });
          if (shares.length !== 0) {
            fractionShares = [
              ...fractionShares,
              {
                id: shareType.sharetypeId,
                name: shareType.name,
                shares: shares.sort((a, b) => b.fractions - a.fractions),
              },
            ];
          }
        });
      fractionShares.map((shareType) => {
        if (
          shareType.shares.length % 2 === 0 &&
          shareType.shares[shareType.shares.length / 2].fractions !==
            shareType.shares[shareType.shares.length / 2 - 1].fractions
        ) {
          this.shareReverseSplitAdjustByHighestFractionPossible = true;
          const newReverseSplitForShareholders: ReverseSplitForShareholder[] = [
            ...this.reverseSplitForShareholders,
          ];

          for (let i = 0; i < shareType.shares.length; i++) {
            let newNumberOfSharesAfterAdjustment = 0;
            if (i < shareType.shares.length / 2) {
              newNumberOfSharesAfterAdjustment = Number(
                Math.ceil(shareType.shares[i].numberOfShares).toFixed(2),
              );
            } else {
              newNumberOfSharesAfterAdjustment = Number(
                Math.floor(shareType.shares[i].numberOfShares).toFixed(2),
              );
            }

            const shareholder: ReverseSplitForShareholder | undefined =
              newReverseSplitForShareholders.find(
                (shareholder) => shareholder.id === shareType.shares[i].id,
              );
            const shareholderData: ReverseSplitForShareholderData | undefined =
              shareholder?.data.find((data) => data.shareTypeId === shareType.id);

            if (shareholderData)
              shareholderData.newNumberOfSharesAfterAdjustment = newNumberOfSharesAfterAdjustment;
          }
          this.reverseSplitForShareholders = newReverseSplitForShareholders;
        } else this.shareReverseSplitAdjustByHighestFractionPossible = false;
      });
    }
    this.fractionAdjustmentNameModel.setValue(undefined);
    this.fractionAdjustment = value;
  }

  validate(): boolean {
    this.ratio.validate();
    return this.isValid;
  }

  getShareType(shareTypeId: number) {
    return this.shareTypes.find((shareType) => shareType.sharetypeId === shareTypeId);
  }
}

export default ReverseSplitShareForm;

export type ShareholderWithFraction = {
  shareholderId: number;
  shareholderName: string;
  data: {
    shareTypeId: number;
    shareTypeName: string;
    sharetypeCode: string;
    numberOfShares: number;
    fraction: number;
    editedNumberOfShares: NumberFieldModel;
  }[];
};

export class ReverseSplitShareOverview {
  isOpen = false;

  shareholdersWithFraction: ShareholderWithFraction[];

  constructor(reverseSplitForShareholders: ReverseSplitForShareholder[]) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.shareholdersWithFraction = buildShareholderWithFraction(reverseSplitForShareholders);
  }

  get summaryChangedData() {
    return compact(
      this.shareholdersWithFraction.map((shareholder) => {
        const shareholderData = shareholder.data.filter(
          (d) => d.fraction % 1 !== 0 && d.fraction !== d.editedNumberOfShares.toNumber(),
        );

        if (!shareholderData.length) return undefined;

        return {
          shareholderName: shareholder.shareholderName,
          adjustedShares: shareholderData.map((d) => ({
            shareTypeId: d.shareTypeId,
            fraction: d.fraction,
            editedNumber: d.editedNumberOfShares.parsedValue,
          })),
        };
      }),
    );
  }

  updateAfterChangeSplit(reverseSplitForShareholders: ReverseSplitForShareholder[]) {
    this.shareholdersWithFraction = buildShareholderWithFraction(reverseSplitForShareholders);
  }

  setIsOpen(isOpen: boolean) {
    this.isOpen = isOpen;
  }
}

export function changeReverseSplitRatio(form: ReverseSplitShareForm, ratio: number) {
  let reverseSplitForShareholders: ReverseSplitForShareholder[] = [];

  form.shareholders.map((shareholder) => {
    let newShareholderData: ReverseSplitForShareholderData[] = [];
    form.shareTypes
      .filter(
        (shareType) =>
          sharesForShareholderByShareType(shareholder, shareType, form.sharesForShareholders) > 0,
      )
      .map((shareType) => {
        const numberOfShares = sharesForShareholderByShareType(
          shareholder,
          shareType,
          form.sharesForShareholders,
        );
        const newNumberOfShares = Number((numberOfShares / ratio).toFixed(2));
        newShareholderData = [
          ...newShareholderData,
          {
            numberOfShares: numberOfShares,
            newNumberOfShares: newNumberOfShares,
            newNumberOfSharesAfterAdjustment: newNumberOfShares,
            fractions: numberOfShares % ratio,
            shareTypeId: shareType.sharetypeId,
            shareTypeName: shareType.name,
            sharetypeCode: shareType.shareTypeCode,
          },
        ];
      });

    reverseSplitForShareholders = [
      ...reverseSplitForShareholders,
      {
        id: shareholder.id,
        name: shareholder.name,
        data: newShareholderData,
        shareTypesContainingFractions: newShareholderData
          .filter((d) => d.fractions !== 0)
          .map((d) => d.sharetypeCode),
      },
    ];
  });

  return {
    reverseSplitForShareholders,
  };
}

// build object for applying
export function buildFractionShareAdjustments(
  shareholderWithFractions: ShareholderWithFraction[],
): FractionShareAdjustment[] {
  return shareholderWithFractions.map((shareholder) => ({
    shareholderId: shareholder.shareholderId,
    sharetypesAdjustment: shareholder.data.map((d) => ({
      sharetypeId: d.shareTypeId,
      currentNumberOfShares: d.numberOfShares,
      adjustedToNumberOfShares: d.editedNumberOfShares.toNumber() ?? 0,
    })),
  }));
}

// build object with data for overview component (table)
export function buildReverseSplitForShareholders(
  form: ReverseSplitShareForm,
  fractionShareAdjustment: FractionShareAdjustment[],
): ReverseSplitForShareholder[] {
  return compact(
    fractionShareAdjustment.map((fraction) => {
      const shareholder = form.shareholders.find(
        (shareholder) => shareholder.id === fraction.shareholderId,
      );

      if (!shareholder) return undefined;

      const shareholderData = compact(
        fraction.sharetypesAdjustment?.map(
          (fractionShareType): ReverseSplitForShareholderData | undefined => {
            const shareType = form.shareTypes.find(
              (shareType) => shareType.sharetypeId === fractionShareType.sharetypeId,
            );

            if (!shareType) return undefined;

            const numberOfShares = sharesForShareholderByShareType(
              shareholder,
              shareType,
              form.sharesForShareholders,
            );
            return {
              numberOfShares: fractionShareType.currentNumberOfShares,
              newNumberOfShares: Number(
                (numberOfShares / (form.parsedRatio ?? DEFAULT_REVERSE_SPLIT_RATIO)).toFixed(2),
              ),
              newNumberOfSharesAfterAdjustment: fractionShareType.adjustedToNumberOfShares,
              shareTypeId: shareType.sharetypeId,
              shareTypeName: shareType.name,
              sharetypeCode: shareType.shareTypeCode,
              fractions: numberOfShares % (form.parsedRatio ?? DEFAULT_REVERSE_SPLIT_RATIO),
            };
          },
        ),
      );
      return {
        id: shareholder.id,
        name: shareholder.name,
        data: shareholderData,
        shareTypesContainingFractions: shareholderData
          .filter((d) => d.fractions !== 0)
          .map((d) => d.sharetypeCode),
      };
    }),
  );
}

export function generateNameButtonsForFractionAdjustment(form: ReverseSplitShareForm): string[] {
  return form.shareholders
    .filter((shareholder) =>
      [
        ...new Set(
          form.reverseSplitForShareholders.flatMap(
            (shareholder) => shareholder.shareTypesContainingFractions,
          ),
        ),
      ].every(
        (fractionShareTypes) =>
          sharesForShareholderByShareTypeCode(
            shareholder,
            fractionShareTypes,
            form.sharesForShareholders,
          ) > 0,
      ),
    )
    .map((shareholder) => {
      return shareholder.name;
    });
}

export function calculateEditedNumberOfShares(
  shareholdersWithFraction: ShareholderWithFraction[],
  shareType: ShareType,
  numberOfShares: number,
) {
  return numberOfShares !== 0
    ? shareholdersWithFraction.reduce(
        (sum, shareholder) =>
          sum +
          (shareholder.data
            .find((data) => data.shareTypeId === shareType.sharetypeId)
            ?.editedNumberOfShares.toNumber() ?? 0),
        0,
      )
    : 0;
}

export function buildShareholderWithFraction(
  reverseSplitForShareholder: ReverseSplitForShareholder[],
): ShareholderWithFraction[] {
  return reverseSplitForShareholder.map((shareholder) => ({
    shareholderId: shareholder.id,
    shareholderName: shareholder.name,
    data: shareholder.data.map((d) => ({
      shareTypeId: d.shareTypeId,
      shareTypeName: d.shareTypeName,
      sharetypeCode: d.sharetypeCode,
      numberOfShares: d.numberOfShares,
      fraction: d.newNumberOfShares,
      editedNumberOfShares: new NumberFieldModel({
        value: d.newNumberOfSharesAfterAdjustment,
        isPositiveOrZero: true,
        required: true,
      }),
    })),
  }));
}
